import React, { useMemo, useState } from 'react';
import { ArrowLeft, Users, Zap, CreditCard, ChevronDown, Pen, Trash, Plus, Minus, Check, UserPlus, ShoppingBag, X, Smartphone, Share2, DollarSign, AlertCircle } from 'lucide-react';
import { Product } from '../../types';
import { QRCodeDisplay } from '../QRCodeDisplay';
const formatINR = (amount: number): string => {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};
interface CheckoutScreenProps {
  items: Product[];
  onBack: () => void;
  onPlaceOrder: (billSplitData?: {
    participants: Array<{
      id: string;
      name: string;
      items: Array<{
        name: string;
        price: number;
      }>;
      total: number;
    }>;
    orderTotal: number;
    tax: number;
    isSplitEqually?: boolean;
  }) => void;
}
interface OrderItem extends Product {
  quantity: number;
  assignedTo: string[];
}
interface Participant {
  id: string;
  name: string;
}
type PaymentMethod = 'card' | 'upi';
export function CheckoutScreen({
  items,
  onBack,
  onPlaceOrder
}: CheckoutScreenProps) {
  const [orderItems, setOrderItems] = useState<OrderItem[]>(items.map(item => ({
    ...item,
    quantity: (item as any).quantity || 1,
    assignedTo: []
  })));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('upi');
  const [selectedUpiApp, setSelectedUpiApp] = useState<string>('gpay');
  const [isGroupOrder, setIsGroupOrder] = useState(false);
  const [isSplitEqually, setIsSplitEqually] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([{
    id: '1',
    name: 'You'
  }]);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [isExpressPickup, setIsExpressPickup] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showSpecialInstructions, setShowSpecialInstructions] = useState(false);
  const [lastSpecialInstructions, setLastSpecialInstructions] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const groupSessionId = useMemo(() => `KRAYA-GRP-${Math.random().toString(36).substring(2, 8).toUpperCase()}`, []);
  const mockContacts = [{
    id: '1',
    name: 'Priya Sharma',
    phone: '+91 98765 43210',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop'
  }, {
    id: '2',
    name: 'Rahul Verma',
    phone: '+91 98765 43211',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop'
  }, {
    id: '3',
    name: 'Ananya Patel',
    phone: '+91 98765 43212',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop'
  }, {
    id: '4',
    name: 'Rohan Mehta',
    phone: '+91 98765 43213',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop'
  }];
  const defaultPayment = {
    type: 'Visa',
    last4: '4242',
    expiry: '04/24'
  };
  const upiApps = [{
    id: 'gpay',
    name: 'Google Pay',
    icon: '💳'
  }, {
    id: 'phonepe',
    name: 'PhonePe',
    icon: '💜'
  }, {
    id: 'paytm',
    name: 'Paytm',
    icon: '💙'
  }, {
    id: 'upi',
    name: 'Other UPI',
    icon: '🔷'
  }];
  const subtotal = orderItems.reduce((sum, item) => {
    return sum + item.price * item.quantity;
  }, 0);
  const gst = subtotal * 0.05;
  const total = subtotal + gst;
  // Slab-based Express Order pricing
  const expressPickupFee = (() => {
    if (total < 300) return 59;
    if (total <= 700) return 79;
    return 99;
  })();
  const generateBillSplitData = () => {
    if (isSplitEqually) {
      const totalWithFees = total + (isExpressPickup ? expressPickupFee : 0);
      const perPersonAmount = totalWithFees / participants.length;
      return {
        participants: participants.map(p => ({
          id: p.id,
          name: p.name,
          items: [],
          total: perPersonAmount
        })),
        orderTotal: totalWithFees,
        tax: gst,
        isSplitEqually: true
      };
    } else {
      // Calculate split prices for each participant
      const participantsData = participants.map(participant => {
        const participantItems: Array<{
          name: string;
          price: number;
        }> = [];
        orderItems.forEach(item => {
          if (item.assignedTo.includes(participant.id)) {
            // Calculate the split price: (item price × quantity) ÷ number of people assigned
            const totalItemCost = item.price * item.quantity;
            const splitPrice = totalItemCost / item.assignedTo.length;
            participantItems.push({
              name: item.name,
              price: splitPrice
            });
          }
        });
        const participantTotal = participantItems.reduce((sum, item) => sum + item.price, 0);
        const participantGST = participantTotal / subtotal * gst;
        return {
          id: participant.id,
          name: participant.name,
          items: participantItems,
          total: participantTotal + participantGST
        };
      });
      return {
        participants: participantsData,
        orderTotal: total + (isExpressPickup ? expressPickupFee : 0),
        tax: gst,
        isSplitEqually: false
      };
    }
  };
  const addParticipantFromQuickAdd = () => {
    if (quickAddName.trim()) {
      setParticipants([...participants, {
        id: Date.now().toString(),
        name: quickAddName.trim()
      }]);
      setQuickAddName('');
      setShowBottomSheet(false);
    }
  };
  const addParticipantFromContact = (contact: (typeof mockContacts)[0]) => {
    setParticipants([...participants, {
      id: Date.now().toString(),
      name: contact.name
    }]);
    setShowBottomSheet(false);
  };
  const removeParticipant = (id: string) => {
    setParticipants(participants.filter(p => p.id !== id));
    setOrderItems(orderItems.map(item => ({
      ...item,
      assignedTo: item.assignedTo.filter(pId => pId !== id)
    })));
  };
  const assignItemToParticipant = (itemName: string, participantId: string) => {
    setOrderItems(orderItems.map(item => {
      if (item.name === itemName) {
        const isAssigned = item.assignedTo.includes(participantId);
        return {
          ...item,
          assignedTo: isAssigned ? item.assignedTo.filter(id => id !== participantId) : [...item.assignedTo, participantId]
        };
      }
      return item;
    }));
  };
  const calculateParticipantTotal = (participantId: string) => {
    return orderItems.reduce((sum, item) => {
      if (item.assignedTo.includes(participantId)) {
        // Calculate split price: (item price × quantity) ÷ number of people assigned
        const totalItemCost = item.price * item.quantity;
        const splitPrice = totalItemCost / item.assignedTo.length;
        return sum + splitPrice;
      }
      return sum;
    }, 0);
  };
  const removeItem = (index: number) => setOrderItems(orderItems.filter((_, i) => i !== index));
  const clearCart = () => setOrderItems([]);
  const handlePlaceOrderOnly = () => onPlaceOrder();
  const handlePlaceOrderAndSplit = () => {
    if (!isSplitEqually) {
      const unassignedItems = orderItems.filter(item => item.assignedTo.length === 0);
      if (unassignedItems.length > 0) {
        setShowWarningModal(true);
        return;
      }
    }
    onPlaceOrder(generateBillSplitData());
  };
  return <div className="pb-24 min-h-screen bg-gray-50" data-id="element-355">
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-6 flex items-center gap-3 sticky top-0 z-10" data-id="element-356">
        <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-357">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-358" />
        </button>
        <h1 className="text-2xl font-bold text-white" data-id="element-359">Checkout</h1>
      </div>

      <div className="flex-1 px-5 py-4" data-id="element-360">
        {/* Group Order Toggle */}
        <div className="mb-6" data-id="element-361">
          <button onClick={() => setIsGroupOrder(!isGroupOrder)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isGroupOrder ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-362">
            <div className="flex items-center" data-id="element-363">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${isGroupOrder ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-364">
                <Users size={20} className="text-black" data-id="element-365" />
              </div>
              <div className="text-left" data-id="element-366">
                <h3 className="font-medium" data-id="element-367">Group Order</h3>
                <p className="text-sm text-gray-500" data-id="element-368">
                  Split items & share payment requests
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isGroupOrder ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-369">
              {isGroupOrder && <Check size={14} className="text-black" data-id="element-370" />}
            </div>
          </button>
        </div>

        {/* Group Order Section */}
        {isGroupOrder && <div className="mb-6 space-y-4" data-id="element-371">
            <div className="flex items-center justify-between" data-id="element-372">
              <div data-id="element-373">
                <h2 className="text-lg font-bold" data-id="element-374">Create a Group</h2>
                <p className="text-sm text-gray-500" data-id="element-375">add participants</p>
              </div>
              <button onClick={() => setShowBottomSheet(true)} className="bg-[#FFFC00] text-black p-2 rounded-full hover:bg-[#F7C300] transition-all" data-id="element-376">
                <UserPlus size={20} data-id="element-377" />
              </button>
            </div>

            <div className="space-y-2" data-id="element-378">
              {participants.map(participant => {
            const total = calculateParticipantTotal(participant.id);
            const hasItems = orderItems.some(item => item.assignedTo.includes(participant.id));
            return <div key={participant.id} className={`flex items-center justify-between p-3 rounded-xl transition-all ${hasItems && !isSplitEqually ? 'bg-[#F0EEFF]' : 'bg-gray-100'}`} data-id="element-379">
                    <div className="flex items-center" data-id="element-380">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${hasItems && !isSplitEqually ? 'bg-[#FFFBE6]' : 'bg-gray-200'}`} data-id="element-381">
                        <span className="text-sm font-medium" data-id="element-382">
                          {participant.name[0].toUpperCase()}
                        </span>
                      </div>
                      <div data-id="element-383">
                        <p className="font-medium" data-id="element-384">{participant.name}</p>
                        {!isSplitEqually && hasItems ? <p className="text-sm text-[#A020F0]" data-id="element-385">
                            {formatINR(total)}
                          </p> : isSplitEqually ? <p className="text-sm text-[#A020F0]" data-id="element-386">
                            {formatINR((total + gst + (isExpressPickup ? expressPickupFee : 0)) / participants.length)}
                          </p> : <p className="text-sm text-gray-500" data-id="element-387">No items yet</p>}
                      </div>
                    </div>
                    {participant.id !== '1' && <button onClick={() => removeParticipant(participant.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-388">
                        <Trash size={18} data-id="element-389" />
                      </button>}
                  </div>;
          })}
            </div>

            {/* Equal Split Toggle - Only show when there are multiple participants */}
            {participants.length > 1 && <button onClick={() => setIsSplitEqually(!isSplitEqually)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSplitEqually ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-390">
                <div className="flex items-center" data-id="element-391">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${isSplitEqually ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-392">
                    <DollarSign size={20} className="text-black" data-id="element-393" />
                  </div>
                  <div className="text-left" data-id="element-394">
                    <h3 className="font-medium" data-id="element-395">Split Equally</h3>
                    {isSplitEqually ? <p className="text-sm text-[#A020F0] font-medium" data-id="element-396">
                        Each pays:{' '}
                        {formatINR((total + (isExpressPickup ? expressPickupFee : 0)) / participants.length)}
                      </p> : <p className="text-sm text-gray-500" data-id="element-397">
                        Divide total by participants
                      </p>}
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSplitEqually ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-398">
                  {isSplitEqually && <Check size={14} className="text-black" data-id="element-399" />}
                </div>
              </button>}
          </div>}

        {/* Order Items */}
        <div className="mb-6" data-id="element-400">
          <div className="flex items-center justify-between mb-3" data-id="element-401">
            <h2 className="text-lg font-bold" data-id="element-402">Order Items</h2>
            {orderItems.length > 0 && <button onClick={clearCart} className="text-gray-500 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-gray-100" title="Clear Cart" data-id="element-403">
                <Trash size={18} data-id="element-404" />
              </button>}
          </div>

          {orderItems.length === 0 ? <div className="bg-[#F0EEFF] rounded-xl p-6 text-center" data-id="element-405">
              <ShoppingBag size={24} className="mx-auto mb-2 text-gray-400" data-id="element-406" />
              <p className="text-gray-500" data-id="element-407">Your cart is empty</p>
              <button onClick={() => onBack()} className="mt-4 text-sm text-[#A020F0] font-medium hover:underline" data-id="element-408">
                Return to Menu
              </button>
            </div> : <div className="space-y-3" data-id="element-409">
              {orderItems.map((item, index) => <div key={index} className="bg-[#F0EEFF] rounded-xl p-4" data-id="element-410">
                  <div className="flex justify-between items-start mb-2" data-id="element-411">
                    <div className="flex-1" data-id="element-412">
                      <div className="flex items-start justify-between" data-id="element-413">
                        <div data-id="element-414">
                          <h3 className="font-medium" data-id="element-415">{item.name}</h3>
                          <p className="text-[#A020F0] font-medium mt-1" data-id="element-416">
                            {formatINR(item.price)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-417">
                          <div className="flex items-center bg-white rounded-lg shadow-sm" data-id="element-418">
                            <button onClick={() => {
                        if (item.quantity > 1) setOrderItems(prev => prev.map((i, idx) => idx === index ? {
                          ...i,
                          quantity: i.quantity - 1
                        } : i));
                      }} className="p-1.5 hover:bg-gray-50 rounded-l-lg transition-colors" disabled={item.quantity <= 1} data-id="element-419">
                              <Minus size={14} className={item.quantity <= 1 ? 'text-gray-300' : 'text-gray-600'} data-id="element-420" />
                            </button>
                            <span className="w-8 text-center text-sm font-medium" data-id="element-421">
                              {item.quantity}
                            </span>
                            <button onClick={() => setOrderItems(prev => prev.map((i, idx) => idx === index ? {
                        ...i,
                        quantity: i.quantity + 1
                      } : i))} className="p-1.5 hover:bg-gray-50 rounded-r-lg transition-colors" data-id="element-422">
                              <Plus size={14} className="text-gray-600" data-id="element-423" />
                            </button>
                          </div>
                          <p className="font-medium min-w-[60px] text-right" data-id="element-424">
                            {formatINR(item.price * item.quantity)}
                          </p>
                          <button onClick={() => removeItem(index)} className="text-gray-400 hover:text-red-500 transition-colors p-1.5 hover:bg-white rounded-lg" title="Remove Item" data-id="element-425">
                            <Trash size={16} data-id="element-426" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isGroupOrder && !isSplitEqually && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-427">
                      <div className="flex justify-between items-center mb-2" data-id="element-428">
                        <p className="text-sm text-gray-500" data-id="element-429">Assign to:</p>
                        {item.assignedTo.length === 0 && <p className="text-xs text-amber-600" data-id="element-430">
                            Not assigned yet
                          </p>}
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-431">
                        {participants.map(participant => {
                  const isAssigned = item.assignedTo.includes(participant.id);
                  return <button key={participant.id} onClick={() => assignItemToParticipant(item.name, participant.id)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${isAssigned ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} flex items-center space-x-1`} data-id="element-432">
                              <span data-id="element-433">{participant.name}</span>
                              {isAssigned && <Check size={14} className="ml-1" data-id="element-434" />}
                            </button>;
                })}
                      </div>
                      {item.assignedTo.length > 0 && <div className="mt-2 space-y-1" data-id="element-435">
                          <div className="text-xs text-gray-500" data-id="element-436">
                            {item.assignedTo.length}{' '}
                            {item.assignedTo.length === 1 ? 'person' : 'people'}{' '}
                            selected
                          </div>
                          <div className="text-xs font-medium text-[#A020F0]" data-id="element-437">
                            {formatINR(item.price * item.quantity)} ÷{' '}
                            {item.assignedTo.length} ={' '}
                            {formatINR(item.price * item.quantity / item.assignedTo.length)}{' '}
                            each
                          </div>
                        </div>}
                    </div>}
                </div>)}
            </div>}
        </div>

        {/* Payment Method */}
        <div className="mb-6" data-id="element-438">
          <h2 className="text-lg font-bold mb-3" data-id="element-439">Payment Method</h2>
          <div className="flex gap-2 mb-4" data-id="element-440">
            <button onClick={() => setPaymentMethod('card')} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'card' ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600'}`} data-id="element-441">
              <CreditCard size={18} data-id="element-442" />
              Card
            </button>
            <button onClick={() => setPaymentMethod('upi')} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'upi' ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600'}`} data-id="element-443">
              <Smartphone size={18} data-id="element-444" />
              UPI
            </button>
          </div>

          {paymentMethod === 'card' && <div className="bg-[#F0EEFF] rounded-xl p-4" data-id="element-445">
              <div className="flex items-center justify-between" data-id="element-446">
                <div className="flex items-center" data-id="element-447">
                  <CreditCard className="w-5 h-5 mr-3 text-[#A020F0]" data-id="element-448" />
                  <div data-id="element-449">
                    <h3 className="font-medium" data-id="element-450">
                      {defaultPayment.type} ****{defaultPayment.last4}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-451">
                      Expires {defaultPayment.expiry}
                    </p>
                  </div>
                </div>
                <button className="text-[#A020F0] text-sm font-medium" data-id="element-452">
                  Change
                </button>
              </div>
            </div>}

          {paymentMethod === 'upi' && <div className="space-y-3" data-id="element-453">
              <div className="grid grid-cols-2 gap-3" data-id="element-454">
                {upiApps.map(app => <button key={app.id} onClick={() => setSelectedUpiApp(app.id)} className={`p-4 rounded-xl border-2 transition-all text-left ${selectedUpiApp === app.id ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-200 bg-white'}`} data-id="element-455">
                    <div className="flex items-center gap-3" data-id="element-456">
                      <span className="text-2xl" data-id="element-457">{app.icon}</span>
                      <div className="flex-1" data-id="element-458">
                        <p className="font-medium text-sm" data-id="element-459">{app.name}</p>
                      </div>
                      {selectedUpiApp === app.id && <Check size={18} className="text-[#A020F0]" data-id="element-460" />}
                    </div>
                  </button>)}
              </div>
              {selectedUpiApp === 'upi' && <div className="bg-[#F0EEFF] rounded-xl p-4 animate-in fade-in duration-200" data-id="element-461">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-462">
                    Enter UPI ID
                  </label>
                  <input type="text" placeholder="yourname@upi" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-[#FFFC00] outline-none" data-id="element-463" />
                </div>}
            </div>}
        </div>

        {/* Express Order Toggle */}
        <div className="mb-6" data-id="element-464">
          <button onClick={() => setIsExpressPickup(!isExpressPickup)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isExpressPickup ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-465">
            <div className="flex items-center" data-id="element-466">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${isExpressPickup ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-467">
                <Zap size={20} className="text-black" data-id="element-468" />
              </div>
              <div className="text-left" data-id="element-469">
                <h3 className="font-medium" data-id="element-470">Express Order</h3>
                <p className="text-sm text-gray-500" data-id="element-471">
                  Prioritize order (+{formatINR(expressPickupFee)})
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isExpressPickup ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-472">
              {isExpressPickup && <Check size={14} className="text-black" data-id="element-473" />}
            </div>
          </button>
        </div>

        {/* Order Summary */}
        <div className="mb-6" data-id="element-474">
          <button onClick={() => setShowOrderSummary(!showOrderSummary)} className="w-full flex items-center justify-between mb-2" data-id="element-475">
            <h2 className="text-lg font-bold" data-id="element-476">Order Summary</h2>
            <ChevronDown size={20} className={`text-gray-400 transition-transform ${showOrderSummary ? 'rotate-180' : ''}`} data-id="element-477" />
          </button>
          {showOrderSummary && <div className="space-y-3 animate-in fade-in duration-200" data-id="element-478">
              {orderItems.map((item, index) => <div key={index} className="flex justify-between text-sm" data-id="element-479">
                  <span data-id="element-480">
                    {item.quantity}x {item.name}
                  </span>
                  <span data-id="element-481">{formatINR(item.price * item.quantity)}</span>
                </div>)}
              <div className="border-t border-gray-100 pt-3 space-y-2" data-id="element-482">
                <div className="flex justify-between text-sm" data-id="element-483">
                  <span data-id="element-484">Subtotal</span>
                  <span data-id="element-485">{formatINR(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-486">
                  <span data-id="element-487">GST (5%)</span>
                  <span data-id="element-488">{formatINR(gst)}</span>
                </div>
                {isExpressPickup && <div className="flex justify-between text-sm" data-id="element-489">
                    <span data-id="element-490">Express Order</span>
                    <span data-id="element-491">{formatINR(expressPickupFee)}</span>
                  </div>}
              </div>
            </div>}
        </div>

        {/* Special Instructions */}
        <div className="mb-8" data-id="element-492">
          <button onClick={() => setShowSpecialInstructions(!showSpecialInstructions)} className="w-full flex items-center justify-between mb-2" data-id="element-493">
            <div className="flex items-center" data-id="element-494">
              <h2 className="text-lg font-bold" data-id="element-495">Special Instructions</h2>
              {lastSpecialInstructions && !showSpecialInstructions && <span className="ml-2 text-sm text-gray-500" data-id="element-496">
                  ({lastSpecialInstructions})
                </span>}
            </div>
            <Pen size={18} className="text-gray-400" data-id="element-497" />
          </button>
          {showSpecialInstructions && <div className="animate-in fade-in duration-200" data-id="element-498">
              <textarea defaultValue={lastSpecialInstructions} onChange={e => setLastSpecialInstructions(e.target.value)} placeholder="Add special instructions..." className="w-full p-3 rounded-xl border-2 border-gray-100 focus:border-[#FFFC00] outline-none h-24 text-sm" data-id="element-499" />
            </div>}
        </div>

        {/* Checkout Buttons */}
        {isGroupOrder ? <div className="space-y-3" data-id="element-500">
            <button onClick={handlePlaceOrderOnly} disabled={orderItems.length === 0} className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 ${orderItems.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFFC00] text-black hover:bg-[#F7C300]'}`} data-id="element-501">
              Place Order (
              {formatINR(total + (isExpressPickup ? expressPickupFee : 0))})
            </button>
            <button onClick={handlePlaceOrderAndSplit} disabled={orderItems.length === 0} className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 flex items-center justify-center gap-2 ${orderItems.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'}`} data-id="element-502">
              <Share2 size={20} data-id="element-503" />
              Split Bill & Share
            </button>
          </div> : <button onClick={handlePlaceOrderOnly} disabled={orderItems.length === 0} className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 ${orderItems.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFFC00] text-black hover:bg-[#F7C300]'}`} data-id="element-504">
            Place Order (
            {formatINR(total + (isExpressPickup ? expressPickupFee : 0))})
          </button>}
      </div>

      {/* Bottom Sheet Modal */}
      {showBottomSheet && <>
          <div className="absolute inset-0 bg-black/50 z-40 animate-in fade-in duration-200" onClick={() => setShowBottomSheet(false)} data-id="element-505" />
          <div className="absolute inset-0 z-50 flex items-center justify-center p-4 pb-16" data-id="element-506">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-h-[70vh] flex flex-col animate-in zoom-in-95 fade-in duration-200" data-id="element-507">
              <div className="flex-1 overflow-y-auto px-5 pb-5" data-id="element-508">
                <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pt-2 pb-1 z-10" data-id="element-509">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-510">
                    Add to Group
                  </h2>
                  <button onClick={() => setShowBottomSheet(false)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-id="element-511">
                    <X size={20} className="text-gray-500" data-id="element-512" />
                  </button>
                </div>

                {/* QR Code Section */}
                <div className="mb-5 text-center" data-id="element-513">
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-3" data-id="element-514">
                    Scan to Join
                  </h3>
                  <div className="flex justify-center" data-id="element-515">
                    <div className="transform scale-75 origin-top" data-id="element-516">
                      <QRCodeDisplay value={groupSessionId} data-id="element-517" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 -mt-6" data-id="element-518">
                    Friends can scan this code to join instantly
                  </p>
                </div>

                <div className="relative mb-5" data-id="element-519">
                  <div className="absolute inset-0 flex items-center" data-id="element-520">
                    <div className="w-full border-t border-gray-200" data-id="element-521"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-522">
                    <span className="bg-white px-3 text-xs text-gray-400" data-id="element-523">
                      or add manually
                    </span>
                  </div>
                </div>

                <div className="mb-5" data-id="element-524">
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-2" data-id="element-525">
                    Quick Add
                  </h3>
                  <div className="flex gap-2" data-id="element-526">
                    <input type="text" placeholder="Enter name..." value={quickAddName} onChange={e => setQuickAddName(e.target.value)} className="flex-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-[#FFFC00] outline-none text-sm" onKeyPress={e => {
                  if (e.key === 'Enter') addParticipantFromQuickAdd();
                }} data-id="element-527" />
                    <button onClick={addParticipantFromQuickAdd} disabled={!quickAddName.trim()} className="px-5 py-2.5 bg-[#FFFC00] text-black font-bold rounded-xl hover:bg-[#F7C300] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm" data-id="element-528">
                      Add
                    </button>
                  </div>
                </div>

                <div data-id="element-529">
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-2" data-id="element-530">
                    From Contacts
                  </h3>
                  <div className="space-y-1.5" data-id="element-531">
                    {mockContacts.map(contact => <button key={contact.id} onClick={() => addParticipantFromContact(contact)} className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left" data-id="element-532">
                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0" data-id="element-533">
                          <img src={contact.avatar} alt={contact.name} className="w-full h-full object-cover" data-id="element-534" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-535">
                          <p className="font-bold text-gray-900 truncate text-sm" data-id="element-536">
                            {contact.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-537">
                            {contact.phone}
                          </p>
                        </div>
                        <div className="w-7 h-7 rounded-full bg-[#FFFC00] flex items-center justify-center flex-shrink-0" data-id="element-538">
                          <Plus size={16} className="text-black" data-id="element-539" />
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Warning Modal */}
      {showWarningModal && <>
          <div className="fixed inset-0 bg-black/60 z-50 animate-in fade-in duration-200" onClick={() => setShowWarningModal(false)} data-id="element-540" />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-sm animate-in zoom-in-95 duration-200" data-id="element-541">
            <div className="bg-white rounded-2xl p-6 shadow-2xl" data-id="element-542">
              <div className="flex items-start gap-4 mb-4" data-id="element-543">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-544">
                  <AlertCircle size={24} className="text-amber-600" data-id="element-545" />
                </div>
                <div className="flex-1" data-id="element-546">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-547">
                    Items Not Assigned
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-548">
                    Please assign all items to participants before placing your
                    order.
                  </p>
                </div>
              </div>
              <button onClick={() => setShowWarningModal(false)} className="w-full py-3 bg-[#FFFC00] text-black font-bold rounded-xl hover:bg-[#F7C300] transition-all" data-id="element-549">
                Got it
              </button>
            </div>
          </div>
        </>}
    </div>;
}